import {
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useTheme,
  Dialog,
} from "@mui/material";
import React, { JSX, useCallback, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useAppDispatch } from "../../app/hooks";
import { Formik, Form } from "formik";
import DropDownAPI, { WorkOrderQueueAPI } from "../QueueBoard/api/workOrderApi";
import {
  setWorkOrderCurrentTab,
  setCurrentWorkOrder,
} from "../store/queueBoardSlice";
import {
  Segment,
  WorkOrder,
  WorkOrderArchive,
  WorkOrderHistory,
} from "../QueueBoard/models/workOrderTypes";
import Swal from "sweetalert2";
import WorkOrderSegmentList from "./segment/WorkOrderSegmentList";
import WorkOrderDialog from "./WorkOrderFormDialog";
import {
  GetActiveInactiveTabColorStyle,
  ButtonStyleOutlined,
  ButtonStyle,
} from "../QueueBoard/models/workOrderStyles";
import "./styles/work-order-dialog-container.css";
import localStorageUtil from "../../app/localStorage";
import { User } from "../authentication/models/authTypes";
import { useWorkOrderStore } from "../store/zustandWorkOrderStore";
import { useLookupData } from "../common/hooks/useLookupData";
import { AccessTime } from "@mui/icons-material";
import { capitalizeFirstLetter } from "../common/Utils/commonUtilityFunctions";
import { VIEW } from "../common/constants/filterParamConstants";
import { WorkOrderSchema } from "components/QueueBoard/models/workOrderSchema";

import {
  flexRowCenter,
  flexColumnFlexStart,
  woDocNumberStyle,
  ClockingSectionWrapperStyle,
  normalStyle,
  headingStyle,
  progressBarWrapperStyle,
} from "./styles/dialogContainerStyles";
import WorkOrderHistoryDialog from "./activity/WorkOrderActivityMain";
import { TrackedDialog } from "components/common/dialogs/DialogForTrackingIdleState";
import { ConcurrencyError } from "../common/Utils/concurrencyError";
import {
  isEmbeddedInDesktop,
  isRunningInWebView2,
  openWorkOrderInDesktop,
  subscribeToDesktopHostMessages,
} from "../common/Utils/webViewBridge";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import { useSnackbar } from "notistack";

import packageJson from "../../../package.json";
import { useLoading } from "components/common/context/LoadingContext";
import { ensureEditableWorkOrder } from "./utils/finalizationHelpers";
import { ConcurrencyDialog } from "components/common/components/ConcurrencyDialog";

interface WorkOrderDialogContainerProps {
  showHideWoPopup: (isDialogToShow: boolean) => void;
  InvoiceDocIdentity?: number;
  tabIndex?: number;
  open: boolean;
  /**
   * When true, the dialog was opened from a QueueBoard view (Status, Tech, or
   * Priority � incl. card dialog on those boards). Enables board-specific save/close behavior:
   * - Create: cache id, close, refresh board, reopen via useCachedWorkOrderOpen
   * - Update: toast, keep dialog open; refresh board when user closes
   * Leave false for Intake Calendar, Navbar, FilterPanel, etc.
   */
  cacheNewWorkOrderIdOnSave?: boolean;
}

// Cache key for storing user's unsaved changes during concurrency conflicts
const WO_CACHE_KEY = "wo_concurrency_cache";

const EMPTY_USER: User = {
  userName: "",
  defaultLocationIdentity: 0,
  identity: 0,
  isSalesPerson: false,
  userNameAndCompany: "",
  company: "",
  isTechnician: false,
  hasWorkOrderAccess: false,
  hasChecklistTemplateAccess: false,
  hasChecklistAssignmentAccess: false,
  locationAddress: "",
};

function getStoredUser(): User {
  return (localStorageUtil.getByKey("user") as User | undefined) ?? EMPTY_USER;
}

// Helper to cache form values
const cacheFormValues = (invoiceDocId: number, values: any) => {
  try {
    const cacheData = {
      invoiceDocId,
      values,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(WO_CACHE_KEY, JSON.stringify(cacheData));
  } catch (e) {
    console.error("Failed to cache form values:", e);
  }
};

// Helper to get cached form values
const getCachedFormValues = (invoiceDocId: number): any | null => {
  try {
    const cached = sessionStorage.getItem(WO_CACHE_KEY);
    if (cached) {
      const cacheData = JSON.parse(cached);
      // Only return cache if it's for the same work order and less than 5 minutes old
      if (
        cacheData.invoiceDocId === invoiceDocId &&
        Date.now() - cacheData.timestamp < 5 * 60 * 1000
      ) {
        return cacheData.values;
      }
    }
  } catch (e) {
    console.error("Failed to get cached form values:", e);
  }
  return null;
};

// Helper to clear cached form values
const clearCachedFormValues = () => {
  try {
    sessionStorage.removeItem(WO_CACHE_KEY);
  } catch (e) {
    console.error("Failed to clear cached form values:", e);
  }
};

const refreshQueueBoard = () => {
  if (typeof window !== "undefined") {
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("refreshWorkOrders"));
    }, 100);
  }
};

type WorkOrderFormValues = WorkOrder & {
  unitIdentity?: number | null;
  woDescription?: string | null;
};

const normalizeWorkOrderFormValues = (
  workOrder: WorkOrderFormValues,
): WorkOrderFormValues => {
  const unitIdentity = workOrder.unitIdentity ?? workOrder.woUnitIdentity ?? null;

  return {
    ...workOrder,
    unitIdentity,
    woUnitIdentity: unitIdentity,
    woDescription: workOrder.woDescription ?? workOrder.description ?? "",
  };
};

type WorkOrderLoadResult = {
  workOrder: WorkOrder | null;
  history?: WorkOrderHistory[];
};

// Deduplicate concurrent loads for the same work order (e.g. React Strict Mode double-mount).
const inFlightWorkOrderLoads = new Map<number, Promise<WorkOrderLoadResult>>();

async function loadWorkOrderData(
  invoiceDocId: number,
  forceRefresh = false,
): Promise<WorkOrderLoadResult> {
  if (!forceRefresh) {
    const inFlight = inFlightWorkOrderLoads.get(invoiceDocId);
    if (inFlight) {
      return inFlight;
    }
  }

  const loadPromise = (async (): Promise<WorkOrderLoadResult> => {
    const workOrder = await DropDownAPI.getWorkOrder(invoiceDocId);
    let history: WorkOrderHistory[] | undefined;
    if (workOrder?.isWoHistoryExist) {
      history = (await DropDownAPI.getWorkOrderHistory(invoiceDocId)) ?? undefined;
    }
    return { workOrder, history };
  })();

  if (!forceRefresh) {
    inFlightWorkOrderLoads.set(invoiceDocId, loadPromise);
    loadPromise.finally(() => {
      if (inFlightWorkOrderLoads.get(invoiceDocId) === loadPromise) {
        inFlightWorkOrderLoads.delete(invoiceDocId);
      }
    });
  }

  return loadPromise;
}

// Add this helper component at the top (after imports, before WorkOrderDialogContainer)
function FormikValueTracker({ formik, onChange }) {
  React.useEffect(() => {
    onChange(formik.values);
  }, [formik.values, onChange]);
  return null;
}

const WorkOrderDialogContainer = ({
  showHideWoPopup,
  InvoiceDocIdentity,
  tabIndex,
  open,
  cacheNewWorkOrderIdOnSave = false,
}: WorkOrderDialogContainerProps) => {
  const FIXED_DIALOG_WIDTH = 1000;
  const theme = useTheme();
  const dispatch = useAppDispatch();
  useLookupData();
  const { woStatuses, technicians, woPriorityList, salesPersons, locations } =
    useWorkOrderStore();
  const { enqueueSnackbar } = useSnackbar();
  const styles = document.createElement("style");
  styles.innerHTML = `
    .swal2-container {
      z-index: 2000 !important; /* or a value higher than the MUI dialog's z-index */
    }
  `;

  const [showProgressBar, setShowProgressBar] = useState(false);
  const [isActivityLoaded, setIsActivityLoaded] = useState(false);
  const [user] = useState<User>(getStoredUser);
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [userName] = useState<string | "">(
    localStorage.getItem("username") || "",
  );
  const [isUnitSearchInProgress, setIsUnitSearchInProgress] = useState(false);
  const [currentWorkOrder, setCurWo] = useState<WorkOrder>();
  const [currentWOActivity, setCurrentWOActivity] =
    useState<WorkOrderHistory[]>();

  const [isWoHistoryExists, setIsWoHistoryExists] = useState<boolean>(false);
  const [latestFormikValues, setLatestFormikValues] = useState(null);
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
  const [archiveArgs, setArchiveArgs] = useState(null);
  const [showConcurrencyDialog, setShowConcurrencyDialog] = useState(false);
  const [concurrencyMessage, setConcurrencyMessage] = useState("");
  const [cachedValuesToRestore, setCachedValuesToRestore] = useState<any>(null);
  const [showRestoreCacheDialog, setShowRestoreCacheDialog] = useState(false);
  const [pendingCachedValues, setPendingCachedValues] = useState<any>(null);
  const [restoredKey, setRestoredKey] = useState<string | null>(null);
  // Desktop host sets window.__ASPEN_EMBEDDED_DESKTOP__ so we know even before message; also listen for "host:desktop" (sent delayed)
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    isEmbeddedInDesktop(),
  );
  // Optional: handler to do something when Formik values change
  const handleFormikChange = (values) => {
    setLatestFormikValues(values);
  };

  const handleDialogClose = useCallback(
    (options?: { skipBoardRefresh?: boolean }) => {
      if (cacheNewWorkOrderIdOnSave && !options?.skipBoardRefresh) {
        refreshQueueBoard();
      }
      showHideWoPopup(false);
      dispatch(setWorkOrderCurrentTab(0));
    },
    [cacheNewWorkOrderIdOnSave, showHideWoPopup, dispatch],
  );
   
  useEffect(() => {
    if (isEmbeddedInDesktop()) {
      setIsDesktop(true);
    }

    return subscribeToDesktopHostMessages((data) => {
      if (data === "host:desktop") {
        setIsDesktop(true);
      }
    });
  }, []);

  const fetchWorkOrderData = useCallback(
    async (checkForCache: boolean = false, forceRefresh: boolean = false) => {
      if (!InvoiceDocIdentity || InvoiceDocIdentity <= 0) {
        return;
      }

      try {
        setShowProgressBar(true);
        const { workOrder: response, history: historyResponse } =
          await loadWorkOrderData(InvoiceDocIdentity, forceRefresh);
        if (response) {
          setCurWo(response);
          dispatch(setCurrentWorkOrder(response));
          setIsWoHistoryExists(response.isWoHistoryExist);
          if (response.isWoHistoryExist && historyResponse) {
            setCurrentWOActivity(historyResponse);
            setIsActivityLoaded(true);
          } else {
            setCurrentWOActivity(undefined);
            setIsActivityLoaded(false);
          }
          if (checkForCache) {
            const cachedValues = getCachedFormValues(InvoiceDocIdentity);
            if (cachedValues) {
              const mergedValues = {
                ...cachedValues,
                rowVersion: response.rowVersion,
                segments:
                  cachedValues.segments?.map(
                    (cachedSeg: any, index: number) => {
                      const freshSeg = response.segments?.[index];
                      return {
                        ...cachedSeg,
                        rowVersion:
                          freshSeg?.rowVersion || cachedSeg.rowVersion,
                      };
                    },
                  ) || cachedValues.segments,
              };
              setPendingCachedValues(mergedValues);
              setShowRestoreCacheDialog(true);
            }
          }
        }
        setShowProgressBar(false);
      } catch (error) {
        setShowProgressBar(false);
      }
    },
    [InvoiceDocIdentity, dispatch],
  );

  useEffect(() => {
    if (tabIndex !== undefined) {
      setCurrentTabIndex(tabIndex);
    }
  }, [tabIndex]);

  useEffect(() => {
    if (!open || !InvoiceDocIdentity || InvoiceDocIdentity <= 0) {
      return;
    }
    void fetchWorkOrderData();
  }, [open, InvoiceDocIdentity, fetchWorkOrderData]);

  const handleTabChange = (event, newIndex) => {
    setCurrentTabIndex(newIndex);
    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "handleTabChange",
      {
        elapsed_ms: "124",
        title: "Work Order Dialog Container",
        componentName: "work-order-dialog-container.tsx",
        version: packageJson.version,
      },
    );
  };
  const { showLoading, hideLoading } = useLoading();

  // Handle user's choice to restore cached values
  const handleRestoreCacheConfirm = () => {
    if (pendingCachedValues && currentWorkOrder) {
      // Properly merge cached user changes with fresh server data
      // Start with fresh server data as base, then overlay user's cached changes
      const mergedValues = {
        ...currentWorkOrder, // Fresh server data as base (includes all fields)
        ...pendingCachedValues, // User's cached changes overlay
        // Ensure critical server fields are from fresh data
        rowVersion: currentWorkOrder.rowVersion,
        invoiceDocumentIdentity: currentWorkOrder.invoiceDocumentIdentity,
        documentNumber: currentWorkOrder.documentNumber,
        // Merge segments properly - preserve user changes but use fresh rowVersions
        segments:
          pendingCachedValues.segments?.map(
            (cachedSeg: Segment, index: number) => {
              const freshSeg = currentWorkOrder.segments?.[index];
              return {
                ...freshSeg, // Fresh segment data as base
                ...cachedSeg, // User's cached segment changes
                rowVersion: freshSeg?.rowVersion || cachedSeg.rowVersion,
              };
            },
          ) || currentWorkOrder.segments,
      };
      setCachedValuesToRestore(mergedValues);
      // Set a stable key for the restored form (won't change on re-renders)
      setRestoredKey(`restored-${Date.now()}`);
      enqueueSnackbar(
        "Your changes have been restored. You can proceed with saving.",
        { variant: "success", autoHideDuration: 5000 },
      );
    }
    // Clear cache and dialog state regardless of choice
    clearCachedFormValues();
    setPendingCachedValues(null);
    setShowRestoreCacheDialog(false);
  };

  const handleRestoreCacheDecline = () => {
    // User declined - clear cache and use fresh server data
    clearCachedFormValues();
    setPendingCachedValues(null);
    setCachedValuesToRestore(null);
    setRestoredKey(null);
    setShowRestoreCacheDialog(false);
  };

  const handleWorkOrderArchive = async (values) => {
    if (!user.hasWorkOrderAccess) {
      const popUpText = "You do not have permission to archive work order.";
      await Swal.fire({
        icon: "error",
        title: "Access Denied",
        text: popUpText,
        confirmButtonColor: "#d33",
        confirmButtonText: "OK",
      });
      return;
    }
    if (!(await verifyWorkOrderEditable())) {
      return;
    }
    if (!currentWorkOrder) {
      return;
    }
    showLoading();
    const dtNow = new Date().toISOString();
    const objectToSend: WorkOrderArchive = {
      documentNumber: currentWorkOrder.documentNumber,
      invoiceDocumentIdentity: currentWorkOrder.invoiceDocumentIdentity,
      modifiedBy: user.userName,
      modifiedDate: dtNow,
      rowVersion: currentWorkOrder.rowVersion,
    };

    try {
      await WorkOrderQueueAPI.archiveWorkOrder(objectToSend);

      await Swal.fire({
        title: "Success",
        html: `Work order ${currentWorkOrder.documentNumber} has been archived successfully`,
        icon: "success",
        confirmButtonColor: "#2371E7",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      });

      // Clear cache on successful archive
      clearCachedFormValues();
      setCachedValuesToRestore(null);
      setRestoredKey(null);

      handleDialogClose({ skipBoardRefresh: true });
      refreshQueueBoard();

      logAmplitudeEventWithProps(
        user.userNameAndCompany,
        {
          company: user.company,
          user: user.userName,
        },
        "archiveWorkOrder",
        {
          elapsed_ms: "124",
          title: "Archive the Work Order",
          componentName: "WorkOrderDialogContainerMain.tsx",
          version: packageJson.version,
        },
      );
    } catch (error) {
      if (error instanceof ConcurrencyError) {
        // Cache the user's current changes before showing the dialog
        if (latestFormikValues && currentWorkOrder?.invoiceDocumentIdentity) {
          cacheFormValues(
            currentWorkOrder.invoiceDocumentIdentity,
            latestFormikValues,
          );
        }
        setConcurrencyMessage(error.message);
        setShowConcurrencyDialog(true);
      } else {
        await Swal.fire({
          title: "Error",
          html: `Work order archive failed ${error}`,
          icon: "error",
          confirmButtonColor: "#2371E7",
          confirmButtonText: "OK",
          allowOutsideClick: false,
        });
      }
    } finally {
      setShowProgressBar(false);
      hideLoading();
    }
  };

  const handleWorkOrderSave = async (
    values: any,
    isDirty: boolean,
    actionType: "save" | "commit" | "draft" = "save",
  ) => {
    // no permission;
    if (!user.hasWorkOrderAccess) {
      const popUpText = currentWorkOrder
        ? "You do not have permission to update work order."
        : "You do not have permission to create work order.";
      await Swal.fire({
        icon: "error",
        title: "Access Denied",
        text: popUpText,
        confirmButtonColor: "#d33",
        confirmButtonText: "OK",
      });
      return;
    }
    if (!(await verifyWorkOrderEditable())) {
      return;
    }

    //debugger;
    const dtNow = new Date().toISOString(); // format(new Date(new Date()), "MMM d, yyyy, h:mmaaa");
    // Clean date values to ensure empty strings are converted to null
    // Only set committedDate when the action is actually a commit action
    if (actionType === "commit") {
      values.committedDate = dtNow;
      values.actionType = "commit";
      values.status = "committed";
    } else if (actionType === "draft") {
      values.actionType = "draft";
      values.status = "draft";
    }
    const resolvedUnitIdentity =
      values.unitIdentity ?? values.woUnitIdentity ?? null;

    const cleanedValues = {
      ...values,
      unitIdentity: resolvedUnitIdentity,
      woUnitIdentity: resolvedUnitIdentity,
      woDescription: values.woDescription ?? values.description ?? "",
      warrantyDate: values.warrantyDate || null,
      woPickupDate: values.woPickupDate || null,
      woDeliveryDate: values.woDeliveryDate || null,
      scheduleStartTime: values.scheduleStartTime || null,
      scheduleEndTime: values.scheduleEndTime || null,
    };
    const newValues = {
      ...cleanedValues,
      createdBy: currentWorkOrder?.createdBy ?? user.userName,
      createdDate: currentWorkOrder?.createdDate ?? dtNow,
      modifiedBy: user.userName,
      modifiedDate: dtNow,
    };

    setShowProgressBar(true);
    setIsCommitButtonDisabled(true);
    document.head.appendChild(styles);
    try {
      const woCreationResponse = currentWorkOrder
        ? await WorkOrderQueueAPI.updateWorkOrder(newValues)
        : await WorkOrderQueueAPI.createWorkOrder(newValues);

      if (!woCreationResponse) {
        throw new Error("Work order save failed: empty response from server.");
      }

      clearCachedFormValues();
      setCachedValuesToRestore(null);
      setRestoredKey(null);

      const docNumber = woCreationResponse.documentNumber ?? "";
      const savedWorkOrderId = woCreationResponse.invoiceDocumentIdentity;

      if (!currentWorkOrder && cacheNewWorkOrderIdOnSave) {
        // Create from board: cache id, close, refresh, reopen via hook.
        if (savedWorkOrderId) {
          localStorage.setItem(
            "cachedNewWorkOrderId",
            savedWorkOrderId.toString(),
          );
          localStorage.setItem(
            "cachedNewWorkOrderDocumentNumber",
            docNumber,
          );
        }
        handleDialogClose({ skipBoardRefresh: true });
        refreshQueueBoard();
      } else if (currentWorkOrder && cacheNewWorkOrderIdOnSave) {
        // Update from board: toast, stay open, refresh board on close.
        enqueueSnackbar(
          `Work order ${docNumber} has been updated successfully`,
          { variant: "success" },
        );
        setCurWo(woCreationResponse);
        dispatch(setCurrentWorkOrder(woCreationResponse));
        await fetchWorkOrderData(false, true);
      } else {
        await Swal.fire({
          title: "Success",
          html: `Work order ${docNumber} has been ${
            currentWorkOrder ? "updated" : "created"
          } successfully`,
          icon: "success",
          confirmButtonColor: "#2371E7",
          confirmButtonText: "OK",
          allowOutsideClick: false,
        });
        handleDialogClose();
      }
    } catch (error) {
      if (error instanceof ConcurrencyError) {
        // Cache the user's current changes before showing the dialog
        if (latestFormikValues && currentWorkOrder?.invoiceDocumentIdentity) {
          cacheFormValues(
            currentWorkOrder.invoiceDocumentIdentity,
            latestFormikValues,
          );
        }
        setConcurrencyMessage(error.message);
        setShowConcurrencyDialog(true);
      } else {
        await Swal.fire({
          title: "Error",
          html: `Work order ${
            currentWorkOrder ? "update" : "creation"
          } failed ${error}`,
          icon: "error",
          confirmButtonColor: "#2371E7",
          confirmButtonText: "OK",
          allowOutsideClick: false,
        });
      }
    } finally {
      setShowProgressBar(false);
      setIsCommitButtonDisabled(false);
    }

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      (currentWorkOrder ? "Save" : "Update") + "WorkOrder",
      {
        elapsed_ms: "124",
        title: "Work Order Dialog Container",
        componentName: "work-order-dialog-container.tsx",
        version: packageJson.version,
      },
    );
  };

  const handleArchiveClick = (args) => {
    setArchiveArgs(args);
    setConfirmArchiveOpen(true);
  };

  const handleConfirmArchive = async () => {
    setConfirmArchiveOpen(false);
    if (archiveArgs) {
      await handleWorkOrderArchive(archiveArgs);
      setArchiveArgs(null);
    }
  };

  const handleCancelArchive = () => {
    setConfirmArchiveOpen(false);
    setArchiveArgs(null);
  };

  const getDefaultSalesPersonIdentity = () => {
    if (!user.isSalesPerson) {
      return null; // If the customer is not a salesperson, return null
    }

    // Find the salesperson in the list
    const salesPerson = salesPersons.find(
      (person) => person.identity === user.identity,
    );

    return salesPerson ? salesPerson.identity : null; // Return identity if found, else null
  };

  const getDefaultLocationIdentity = () => {
    // Find the salesperson in the list
    const location = locations.find(
      (person) => person.identity === user.defaultLocationIdentity,
    );

    return location ? location.identity : null;
  };

  const getDefaultWostatus = () => {
    const statusView = JSON.parse(
      localStorage.getItem(`${"IsStatusView"}_${userName}`) || "false",
    );
    const statusIdentity = JSON.parse(
      localStorage.getItem("statusIdentity") || "null",
    );
    if (statusView) {
      return (
        woStatuses.find((status) => status.identity === Number(statusIdentity))
          ?.identity || null
      );
    } else {
      return woStatuses.find((status) => status.isDefault)?.identity || null;
    }
  };

  const getDefaultwoStatusText = () => {
    //`${VIEW}_${userName}`

    //const userName = localStorage.getItem("userName") || ""; // Retrieve the username
    const viewKey = `${VIEW}_${userName}`;
    const viewValue = localStorage.getItem(viewKey) || "0"; // Default to "0" (status View)
    const IsStatusView = viewValue === "0"; // Determine view mode

    const statusIdentity = JSON.parse(
      localStorage.getItem("statusIdentity") || "null",
    );
    if (IsStatusView) {
      return (
        woStatuses.find((status) => status.identity === Number(statusIdentity))
          ?.displayText || ""
      );
    } else {
      return woStatuses.find((status) => status.isDefault)?.displayText || "";
    }
  };

  const getDefaultTechnicianIdentity = () => {
    const IsTechView = JSON.parse(
      localStorage.getItem(`${"IsTechView"}_${userName}`) || "false",
    );
    const TechIdentity = JSON.parse(
      localStorage.getItem("techIdentity") || "null",
    );

    // Ensure IsTechView is true and TechIdentity is a valid number
    if (IsTechView && TechIdentity) {
      return (
        technicians?.find(
          (techPerson) => techPerson.identity === Number(TechIdentity),
        )?.identity || null
      );
    }
    return null;
  };

  const handleUnitSearchStart = () => {
    setIsUnitSearchInProgress(true);
  };

  const handleUnitSearchEnd = () => {
    setIsUnitSearchInProgress(false);
  };

  const defaultPriority =
    woPriorityList.find((priority) => priority.isDefault)?.identity || null;
  const defaultstatus = getDefaultWostatus();

  const defaultSalesperson = getDefaultSalesPersonIdentity();
  const defaultLocationIdentity = getDefaultLocationIdentity();
  const [isCommitButtonDisabled, setIsCommitButtonDisabled] = useState(false);
  const defaultTechnicianIdentity = getDefaultTechnicianIdentity();
  const isFinalized =
    (currentWorkOrder?.status || "").trim().toLowerCase() === "finalized";
  const verifyWorkOrderEditable = useCallback(async () => {
    return ensureEditableWorkOrder(
      currentWorkOrder?.invoiceDocumentIdentity,
      enqueueSnackbar,
    );
  }, [currentWorkOrder?.invoiceDocumentIdentity, enqueueSnackbar]);

  return (
    <TrackedDialog
      open={open}
      className="WorkOrderDialogMain"
      PaperProps={{
        sx: {
          borderRadius: 4,
          width: isWoHistoryExists
            ? `min(${FIXED_DIALOG_WIDTH}px, 95vw)`
            : `min(${FIXED_DIALOG_WIDTH - 400}px, 95vw)`,
          maxWidth: "95vw",
          maxHeight: "95vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={(theme) => ({
          m: 0,
          backgroundColor:
            theme.palette.mode === "light"
              ? theme.palette.primary.main
              : "#181F2A",
          color:
            theme.palette.mode === "light"
              ? theme.palette.primary.contrastText
              : "#fff",
          p: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography
            pl="15px"
            sx={{
              fontStyle: "normal",
              fontWeight: 400,
              fontSize: "16px",
            }}
          >
            {currentWorkOrder ? "Edit Work Order" : "New Work Order"}
          </Typography>

          <IconButton
            sx={{ color: "white" }}
            onClick={() => {
              handleDialogClose();
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          className="wo-dialog-body"
          sx={{
            minWidth: isWoHistoryExists
              ? `${FIXED_DIALOG_WIDTH}px`
              : `${FIXED_DIALOG_WIDTH - 400}px`,
            display: isWoHistoryExists ? "flex" : "block",
          }}
        >
          <Box
            className={`wo-dialog-panel${isWoHistoryExists ? " wo-dialog-panel--details" : ""}`}
            sx={isWoHistoryExists ? { p: 3 } : { p: 2 }}
          >
            <Formik
              key={
                restoredKey ||
                currentWorkOrder?.rowVersion ||
                currentWorkOrder?.invoiceDocumentIdentity ||
                "new"
              }
              initialValues={
                cachedValuesToRestore
                  ? normalizeWorkOrderFormValues(cachedValuesToRestore)
                  : currentWorkOrder
                    ? normalizeWorkOrderFormValues(currentWorkOrder)
                    : {
                  technicianIdentity: defaultTechnicianIdentity,
                  locationIdentity: defaultLocationIdentity,
                  salesPersonIdentity: defaultSalesperson,
                  woPriorityIdentity: defaultPriority,
                  woStatusIdentity: defaultstatus,
                  customerIdentity: null,
                  woDisplayText: "",
                  //customerName: null,
                  unitIdentity: null,
                  woUnitIdentity: null,
                  documentNumber: null,
                  invoiceNumber: null,
                  description: null,
                  woDescription: "",
                  hoursClocked: null,
                  meterType: null,
                  meterValue: 0,
                  warrantyDate: null,
                  woPickupDate: null,
                  woDeliveryDate: null,
                  model: null,
                  nonPrintedComment: null,
                  segments: [],
                  //Newly Added
                  purchaseOrderNumber: null,
                  status: getDefaultwoStatusText(),
                  woUnitIsAttachment: false,
                  woUnitIsNew: false,
                  scheduleStartTime: null,
                  scheduleEndTime: null,
                  woUnitBaseSerial: "",
                }
              }
              validationSchema={WorkOrderSchema}
              validateOnChange={true}
              validateOnBlur={true}
              onSubmit={async (values) => {
                await handleWorkOrderSave(values, true, "save");
              }}
              enableReinitialize={true}
            >
              {(formik) => {
                // Shared validation function for all save actions
                const validateAndSave = async (
                  actionType: "save" | "commit" | "draft",
                ) => {
                  // Validate the form
                  const formErrors = await formik.validateForm();

                  if (Object.keys(formErrors).length > 0) {
                    // Mark all error fields as touched to show errors
                    const touchedFields = {};
                    Object.entries(formErrors).forEach(
                      ([fieldName, errorMessage]) => {
                        touchedFields[fieldName] = true;
                      },
                    );
                    formik.setTouched(touchedFields);

                    // Show validation errors
                    Swal.fire({
                      icon: "error",
                      title: "Validation Error",
                      html: `<ul>${Object.values(formErrors)
                        .map(
                          (error) =>
                            `<li style="text-align: left;">${error}</li>`,
                        )
                        .join("")}</ul>`,
                    }).then(() => {
                      //console.log("swal displayed");
                    });
                    document.head.appendChild(styles);
                    return; // Stop execution if validation fails
                  }

                  // Clean date values before submission
                  const cleanedValues = {
                    ...formik.values,
                    warrantyDate: formik.values.warrantyDate || null,
                    woPickupDate: formik.values.woPickupDate || null,
                    woDeliveryDate: formik.values.woDeliveryDate || null,
                    scheduleStartTime: formik.values.scheduleStartTime || null,
                    scheduleEndTime: formik.values.scheduleEndTime || null,
                  };

                  // Proceed with save
                  await handleWorkOrderSave(
                    cleanedValues,
                    formik.dirty,
                    actionType,
                  );
                };

                // Calculate hours (move this up so it's available)
                const calculateTotalHours = () => {
                  let totalElapsedHours = 0;
                  let totalQuotedHours = 0;
                  formik.values.segments?.forEach((item: Segment) => {
                    totalElapsedHours +=
                      parseFloat(item.clockedHours as string) || 0;
                    totalQuotedHours +=
                      parseFloat(item.actualOrFlatRateLaborHrs as string) || 0;
                  });
                  return { totalElapsedHours, totalQuotedHours };
                };
                const { totalElapsedHours, totalQuotedHours } =
                  calculateTotalHours();
                return (
                  <>
                    <FormikValueTracker
                      formik={formik}
                      onChange={handleFormikChange}
                    />
                    <Form
                      className="wo-dialog-form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validateAndSave("save");
                      }}
                    >
                      {/* Work Order header 6928 */}
                      <Box
                        className="6928"
                        sx={{
                          ...flexRowCenter,
                          alignItems: "flex-start",
                          gap: "8px",
                        }}
                      >
                        {/* Work Order Number 6889*/}
                        <Box
                          className="6889"
                          sx={{
                            ...flexColumnFlexStart,
                            p: "0px",
                            width: "500px",
                            minHeight: "65px",
                            gap: "4px",
                          }}
                        >
                          {/* 9126 */}
                          <Box
                            className="9126"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                              gap: "12px",
                            }}
                          >
                            <Typography
                              sx={{
                                ...woDocNumberStyle(theme),
                                flex: 1,
                                minWidth: 0, // Allows text to shrink
                              }}
                            >
                              {formik.values.invoiceNumber}
                            </Typography>
                            {currentWorkOrder && (
                              <Box
                                sx={{
                                  ...flexRowCenter,
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  alignSelf: "center",
                                }}
                                className="6933"
                              >
                                <Chip
                                  label={capitalizeFirstLetter(
                                    currentWorkOrder.status,
                                  )}
                                  size="small"
                                  sx={{
                                    borderRadius: "5px",
                                    color: "white",
                                    backgroundColor: "gray",
                                    fontWeight: "600",
                                    fontSize: "12px",
                                    fontStyle: "normal",
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                          {currentWorkOrder?.createdBy && (
                            <Box>
                              <Box sx={{ ...flexRowCenter, gap: "6px" }}>
                                <Typography sx={normalStyle(theme)}>
                                  Created By:
                                </Typography>
                                <Typography sx={headingStyle(theme)}>
                                  {currentWorkOrder.createdBy}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          {currentWorkOrder?.woUnitBaseSerial && (
                            <Box>
                              <Box sx={{ ...flexRowCenter, gap: "6px" }}>
                                <Typography sx={normalStyle(theme)}>
                                  Base Serial:
                                </Typography>
                                <Typography sx={headingStyle(theme)}>
                                  {currentWorkOrder.woUnitBaseSerial}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                        {/* Clocking Details 7081 */}
                        <Box
                          className="7081"
                          sx={ClockingSectionWrapperStyle(theme)}
                        >
                          <AccessTime />
                          {/* 7085  */}
                          <Box
                            className="7085"
                            sx={{
                              ...flexColumnFlexStart,
                              justifyContent: "center",
                              p: "0px",
                              gap: "6px",
                            }}
                          >
                            <Box
                              className="7083"
                              sx={{
                                ...flexRowCenter,
                                gap: "6px",
                              }}
                            >
                              <Typography sx={normalStyle(theme)}>
                                Quoted:
                              </Typography>
                              <Typography sx={headingStyle(theme)}>
                                {totalQuotedHours === undefined
                                  ? 0
                                  : totalQuotedHours.toFixed(2)}
                                Hrs
                              </Typography>
                            </Box>
                            <Box
                              className="7084"
                              sx={{
                                ...flexRowCenter,
                                gap: "6px",
                              }}
                            >
                              <Typography sx={normalStyle(theme)}>
                                Clocked:
                              </Typography>
                              <Typography sx={headingStyle(theme)}>
                                {totalElapsedHours === undefined
                                  ? 0
                                  : totalElapsedHours.toFixed(2)}
                                Hrs
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                      <Divider sx={{ marginTop: "16px" }} />
                      <Tabs
                        value={currentTabIndex}
                        onChange={handleTabChange}
                        sx={{
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          "& .MuiTabs-indicator": {
                            backgroundColor: theme.palette.primary.main,
                          },
                        }}
                      >
                        <Tab
                          sx={{
                            ...GetActiveInactiveTabColorStyle(
                              currentTabIndex,
                              0,
                              theme,
                            ),
                          }}
                          label="Work Order Details"
                        />
                        <Tab
                          sx={GetActiveInactiveTabColorStyle(
                            currentTabIndex,
                            1,
                            theme,
                          )}
                          label="Work Segments"
                        />
                      </Tabs>
                      {/* Render components based on currentTabIndex using a mapping object */}
                      <Box className="wo-dialog-tab-content">
                        {(() => {
                          const tabComponents: { [key: number]: JSX.Element } = {
                            0: (
                              <WorkOrderDialog
                                isHistoryExists={isWoHistoryExists}
                                formik={formik}
                                onUnitSearchStart={handleUnitSearchStart}
                                onUnitSearchEnd={handleUnitSearchEnd}
                              />
                            ),
                            1: (
                              <WorkOrderSegmentList
                                segments={formik.values.segments}
                                workOrderNumber={formik.values.documentNumber ?? undefined}
                                isFinalized={isFinalized}
                                invoiceDocumentIdentity={
                                  currentWorkOrder?.invoiceDocumentIdentity
                                }
                              />
                            ),
                          };
                          return tabComponents[currentTabIndex];
                        })()}
                      </Box>
                      <DialogActions
                        sx={{
                          padding: "3px 0px 0px",
                          justifyContent: "space-between",
                          width: "100%",
                          flexShrink: 0,
                        }}
                      >
                        {/* Far left: Finalize in POS (desktop only) */}
                        <Box display="flex" gap={1} alignItems="center">
                          {isDesktop && !isFinalized && (
                            <Button
                              sx={{ ...ButtonStyle }}
                              size="small"
                              type="button"
                              name="finalize-pos"
                              variant="contained"
                              disabled={
                                isCommitButtonDisabled ||
                                isUnitSearchInProgress
                              }
                              onClick={async () => {
                                // If running in WebView2 (desktop app), notify the desktop application
                                // Open work order in desktop POS for finalization
                                if (
                                  isRunningInWebView2() &&
                                  currentWorkOrder?.invoiceDocumentIdentity
                                ) {
                                  openWorkOrderInDesktop(
                                    currentWorkOrder?.invoiceDocumentIdentity,
                                  );
                                }
                              }}
                            >
                              Finalize in POS
                            </Button>
                          )}
                        </Box>
                        {/* Right side: Archive + Commit / Save */}
                        {!isFinalized && (
                          <Box display="flex" gap={1} alignItems="center">
                            {currentWorkOrder && (
                              <Button
                                sx={{
                                  ...ButtonStyleOutlined,
                                  backgroundColor: "#d32f2f",
                                  color: "white",
                                  "&:hover": { backgroundColor: "#b71c1c" },
                                }}
                                size="small"
                                variant="contained"
                                onClick={() =>
                                  handleArchiveClick(currentWorkOrder)
                                }
                              >
                                Archive
                              </Button>
                            )}
                            {!currentWorkOrder?.status ||
                            currentWorkOrder?.status === "draft" ? (
                              <Button
                                sx={{ ...ButtonStyle }}
                                size="small"
                                type="button"
                                name="commit"
                                disabled={
                                  isCommitButtonDisabled ||
                                  isUnitSearchInProgress
                                }
                                variant="contained"
                                onClick={async () => {
                                  await formik.setFieldValue(
                                    "status",
                                    "committed",
                                  );
                                  // Validate and save with commit action type
                                  await validateAndSave("commit");
                                }}
                              >
                                Commit
                              </Button>
                            ) : null}
                            {currentWorkOrder ? (
                              <Button
                                disabled={
                                  isCommitButtonDisabled ||
                                  isUnitSearchInProgress
                                }
                                sx={{ ...ButtonStyleOutlined }}
                                size="small"
                                type="submit"
                                name="save"
                                variant="contained"
                              >
                                Save Changes
                              </Button>
                            ) : (
                              <Button
                                sx={{ ...ButtonStyle }}
                                size="small"
                                type="button"
                                name="draft"
                                variant="contained"
                                onClick={async () => {
                                  await formik.setFieldValue("status", "draft");
                                  // Validate and save with draft action type
                                  await validateAndSave("draft");
                                }}
                                disabled={
                                  isCommitButtonDisabled ||
                                  isUnitSearchInProgress
                                }
                              >
                                Save Draft
                              </Button>
                            )}
                          </Box>
                        )}
                      </DialogActions>
                    </Form>
                  </>
                );
              }}
            </Formik>
          </Box>
          {/* Right Side - Work Order Activity */}
          {isWoHistoryExists && isActivityLoaded && (
            <Box
              className="wo-dialog-panel wo-dialog-panel--activity"
              sx={{
                backgroundColor: theme.palette.background.default,
                p: "6px",
              }}
            >
              <WorkOrderHistoryDialog
                setCurrentTabIndex={setCurrentTabIndex}
                activities={currentWOActivity ?? []}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Progress Bar Overlay - Covers entire dialog */}
      {showProgressBar && (
        <Box sx={progressBarWrapperStyle}>
          <CircularProgress sx={{ color: "#2371E7" }} size={48} />
        </Box>
      )}
      {/* Confirmation Dialog for Archive */}
      <Dialog open={confirmArchiveOpen} onClose={handleCancelArchive}>
        <DialogTitle
          sx={(theme) => ({
            backgroundColor:
              theme.palette.mode === "light"
                ? theme.palette.primary.main
                : "#181F2A",
            color:
              theme.palette.mode === "light"
                ? theme.palette.primary.contrastText
                : "#fff",
          })}
        >
          Confirm Archive
        </DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to archive?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelArchive}
            sx={{
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.grey[300]
                  : theme.palette.grey[800],
            }}
            variant="outlined"
          >
            No
          </Button>
          <Button
            onClick={handleConfirmArchive}
            sx={{
              color: theme.palette.getContrastText(theme.palette.error.main),
              backgroundColor: theme.palette.error.main,
              "&:hover": { backgroundColor: theme.palette.error.dark },
            }}
            variant="contained"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Cached Changes Confirmation Dialog */}
      <Dialog
        open={showRestoreCacheDialog}
        onClose={handleRestoreCacheDecline}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor:
              theme.palette.mode === "light"
                ? theme.palette.primary.main
                : "#181F2A",
            color:
              theme.palette.mode === "light"
                ? theme.palette.primary.contrastText
                : "#fff",
            gap: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Typography variant="h6">Restore Previous Changes?</Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography>
            We found your unsaved changes from your last session. Would you like
            to restore them?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            If you choose "No", the changes will be discarded and fresh data
            will be used.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleRestoreCacheDecline}
            variant="outlined"
            color="inherit"
            size="small"
            sx={{ textTransform: "capitalize" }}
          >
            No
          </Button>
          <Button
            onClick={handleRestoreCacheConfirm}
            variant="contained"
            size="small"
            color="primary"
            sx={{ textTransform: "capitalize" }}
          >
            Yes, Restore
          </Button>
        </DialogActions>
      </Dialog>

      {/* Concurrency Conflict Dialog */}
      <ConcurrencyDialog
        open={showConcurrencyDialog}
        onDismiss={() => {
          setShowConcurrencyDialog(false);
          clearCachedFormValues();
          setCachedValuesToRestore(null);
          setRestoredKey(null);
          handleDialogClose();
        }}
        onRefresh={() => {
          setShowConcurrencyDialog(false);
          setCachedValuesToRestore(null);
          setRestoredKey(null);
          fetchWorkOrderData(true, true); // Check for cached values after refresh
        }}
        message={concurrencyMessage}
      />
    </TrackedDialog>
  );
};
export default WorkOrderDialogContainer;
